import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import FileInput from '../../shared/FileInput'
import ConfirmRemovePhotoModal from './ConfirmRemovePhotoModal'
import XmarkIcon from '../../ui/icons/XmarkIcon'

const WRAPPER_CLASS = 'tw:flex tw:flex-col tw:gap-2'
const GRID_CLASS = 'tw:flex tw:flex-wrap tw:gap-2'
const THUMB_WRAP_CLASS = 'tw:relative tw:w-24 tw:h-24'
const THUMB_LINK_CLASS = 'tw:block tw:w-24 tw:h-24'
const THUMB_IMG_CLASS = 'tw:w-24 tw:h-24 tw:object-cover tw:border tw:border-solid '
  + 'tw:border-gray-200 tw:rounded-md! tw:cursor-pointer'
const DELETE_BTN_CLASS = 'tw:absolute tw:top-1 tw:right-1 tw:w-6 tw:h-6 tw:rounded-full! '
  + 'tw:bg-white tw:border tw:border-solid tw:border-gray-300 tw:text-gray-700 '
  + 'tw:flex tw:items-center tw:justify-center tw:cursor-pointer tw:p-0 '
  + 'tw:transition-colors tw:hover:bg-gray-100 tw:hover:text-gray-900 tw:hover:border-gray-400'
const EMPTY_CLASS = 'tw:text-sm tw:text-gray-500 tw:m-0!'

const THUMBNAIL_URL_KEY = 'thumbnail_url'
const SIGNED_ID_KEY = 'signed_id'
const ACCEPT = 'image/png,image/jpeg,image/bmp'

const EMPTY_EXISTING = []
const EMPTY_REMOVED = []
const EMPTY_NEW = []

const newPhotoKey = file => `${file.name}-${file.size}-${file.lastModified}`

const IssuePhotoField = ({
  existingPhotos = EMPTY_EXISTING, removedPhotoIds = EMPTY_REMOVED, newPhotos = EMPTY_NEW,
  onAddPhotos, onRemoveExisting, onRemoveNew
}) => {
  const [previewUrls, setPreviewUrls] = useState([])
  // Confirmed-removal target: { existing: true, signedId } or { existing: false, index }.
  const [pending, setPending] = useState(null)

  useEffect(() => {
    const urls = newPhotos.map(file => URL.createObjectURL(file))
    setPreviewUrls(urls)
    return () => urls.forEach(url => URL.revokeObjectURL(url))
  }, [newPhotos])

  // Single handlers reading the target from data-* — avoids an arrow per item.
  const handleRemoveExistingClick = useCallback(event => {
    setPending({ existing: true, signedId: event.currentTarget.dataset.signedId })
  }, [])
  const handleRemoveNewClick = useCallback(event => {
    setPending({ existing: false, index: Number(event.currentTarget.dataset.index) })
  }, [])
  const handleCancelRemove = useCallback(() => setPending(null), [])
  const handleConfirmRemove = useCallback(() => {
    if (pending?.existing)
      onRemoveExisting(pending.signedId)
    else
      onRemoveNew(pending.index)
    setPending(null)
  }, [pending, onRemoveExisting, onRemoveNew])

  const visibleExisting = existingPhotos.filter(
    photo => !removedPhotoIds.includes(photo[SIGNED_ID_KEY])
  )
  const isEmpty = visibleExisting.length === 0 && newPhotos.length === 0

  return (
    <div className={WRAPPER_CLASS}>
      {isEmpty ? <p className={EMPTY_CLASS}>Aucune photo.</p> : (
        <div className={GRID_CLASS}>
          {visibleExisting.map(photo => (
            <div key={photo[SIGNED_ID_KEY]} className={THUMB_WRAP_CLASS}>
              <a className={THUMB_LINK_CLASS} href={photo.url} rel="noreferrer" target="_blank">
                <img alt="Photo de l’alerte" className={THUMB_IMG_CLASS} src={photo[THUMBNAIL_URL_KEY]} />
              </a>
              <button
                aria-label="Supprimer la photo"
                className={DELETE_BTN_CLASS}
                data-signed-id={photo[SIGNED_ID_KEY]}
                title="Supprimer la photo"
                type="button"
                onClick={handleRemoveExistingClick}
              >
                <XmarkIcon />
              </button>
            </div>
          ))}
          {newPhotos.map((file, index) => (
            <div key={newPhotoKey(file)} className={THUMB_WRAP_CLASS}>
              <a className={THUMB_LINK_CLASS} href={previewUrls[index]} rel="noreferrer" target="_blank">
                <img alt={file.name} className={THUMB_IMG_CLASS} src={previewUrls[index]} />
              </a>
              <button
                aria-label="Supprimer la photo"
                className={DELETE_BTN_CLASS}
                data-index={index}
                title="Supprimer la photo"
                type="button"
                onClick={handleRemoveNewClick}
              >
                <XmarkIcon />
              </button>
            </div>
          ))}
        </div>
      )}
      <FileInput
        accept={ACCEPT}
        buttonClass="btn-primary tw:rounded-md!"
        buttonText="Ajouter des photos"
        multiple
        onChange={onAddPhotos}
      />
      <ConfirmRemovePhotoModal
        isOpen={pending !== null}
        onCancel={handleCancelRemove}
        onConfirm={handleConfirmRemove}
      />
    </div>
  )
}

IssuePhotoField.propTypes = {
  existingPhotos: PropTypes.arrayOf(PropTypes.shape({
    [SIGNED_ID_KEY]: PropTypes.string.isRequired,
    [THUMBNAIL_URL_KEY]: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  })),
  newPhotos: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  onAddPhotos: PropTypes.func.isRequired,
  onRemoveExisting: PropTypes.func.isRequired,
  onRemoveNew: PropTypes.func.isRequired,
  removedPhotoIds: PropTypes.arrayOf(PropTypes.string)
}

IssuePhotoField.defaultProps = {
  existingPhotos: [],
  newPhotos: [],
  removedPhotoIds: []
}

export default IssuePhotoField
